import { apiRequest } from "./apiClient";

const num = (v) => (typeof v === "number" ? v : null);

/**
 * One keyset page of the clinic's frontdesk calls, newest first by default,
 * with the search and chip filters applied on the server.
 *
 * `GET /appointments?clinic_id&cursor&limit&search&status&call_tag&sort` →
 * `data = {items, next_cursor, has_more, limit, total?, counts?}` — `total`
 * and `counts` ({status, call_tag}) come with the first page only. The old
 * bare array is still accepted and read as one complete page.
 *
 * @param {Object} [opts]
 * @param {string} [opts.clinicId] - Admin-only clinic override.
 * @param {string|null} [opts.cursor] - `nextCursor` of the previous page; null for page 1.
 * @param {number} [opts.limit=100]
 * @param {string} [opts.search]
 * @param {string|null} [opts.status] - NEW_CALL | PENDING | HANDLED | RESOLVED | DISCARD
 * @param {string|null} [opts.callTag] - a call tag, or UNTAGGED
 * @param {"desc"|"asc"} [opts.sort]
 * @returns {Promise<{items: Array, nextCursor: string|null, hasMore: boolean, limit: number, total: number|null, counts: Object|null}>}
 */
export const fetchAppointments = async ({
  clinicId,
  cursor,
  limit = 100,
  search,
  status,
  callTag,
  sort,
} = {}) => {
  const params = new URLSearchParams();
  if (clinicId) params.append("clinic_id", clinicId);
  if (cursor) params.append("cursor", cursor);
  params.append("limit", String(limit));
  if (search && search.trim()) params.append("search", search.trim());
  if (status) params.append("status", status);
  if (callTag) params.append("call_tag", callTag);
  if (sort) params.append("sort", sort);

  const response = await apiRequest({
    method: "GET",
    endpoint: `/appointments?${params.toString()}`,
  });

  const data = response?.data ?? response ?? {};
  if (Array.isArray(data)) {
    return { items: data, nextCursor: null, hasMore: false, limit, total: data.length, counts: null };
  }
  return {
    items: Array.isArray(data.items) ? data.items : [],
    nextCursor: data.next_cursor ?? data.nextCursor ?? null,
    hasMore: Boolean(data.has_more ?? data.hasMore),
    limit: num(data.limit) ?? limit,
    total: num(data.total),
    counts: data.counts && typeof data.counts === "object" ? data.counts : null,
  };
};

export const updateAppointmentStatus = async (appointmentId, status) => {
  return await apiRequest({
    method: "POST",
    endpoint: `/appointments/${appointmentId}/status`,
    body: { status },
  });
};

export const updateAppointmentActionNotes = async (
  appointmentId,
  actionNotes
) => {
  return await apiRequest({
    method: "PATCH",
    endpoint: `/appointments/${appointmentId}`,
    body: { action_notes: actionNotes },
  });
};

/**
 * Lift the campaign-call stop that saving a note on this call put on the
 * patient's number. `POST /appointments/<id>/campaign-decline/undo` →
 * `data = {campaign_decline: null, restored: true}`; 404 when there is
 * nothing to undo.
 */
export const undoCampaignDecline = async (appointmentId) => {
  return await apiRequest({
    method: "POST",
    endpoint: `/appointments/${appointmentId}/campaign-decline/undo`,
  });
};

export const updateAppointment = async (appointmentId, fields) => {
  return await apiRequest({
    method: "PATCH",
    endpoint: `/appointments/${appointmentId}`,
    body: fields,
  });
};

export const fetchAppointmentAudio = async (conversationId) => {
  return await apiRequest({
    method: "GET",
    endpoint: `/appointments/${encodeURIComponent(conversationId)}/audio`,
  });
};
